/* eslint-disable no-unused-vars */
import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { Card, List, Progress, Button, Typography, Space, Badge, Tooltip } from 'antd';
import {
  AiOutlineFile,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineMinusCircle,
  AiOutlineUpCircle,
  AiOutlineUpload,
} from 'react-icons/ai';
import { formatFileSize } from 'utils/upload';
import useUpload from 'hooks/useUpload';
import LocaleContext from 'locales';
import { Icon, TextEllipsis } from 'components';

const { Text } = Typography;

const UploadPanelContainer = ({ children }) => {
  // 컨테이너를 위한 DOM 요소
  const containerRoot = document.getElementById('upload-panel-container-root');

  // 컨테이너 요소가 없으면 body에 추가
  React.useEffect(() => {
    if (!containerRoot) {
      const div = document.createElement('div');
      div.id = 'upload-panel-container-root';
      document.body.appendChild(div);
    }

    return () => {
      // 컴포넌트 언마운트 시 정리
      const root = document.getElementById('upload-panel-container-root');
      if (root && root.childNodes.length === 0) {
        document.body.removeChild(root);
      }
    };
  }, []);

  return createPortal(children, containerRoot || document.body);
};

const UploadProgressPanel = () => {
  const state = useUpload({
    userId: localStorage.getItem('userId'),
    userName: localStorage.getItem('username'),
  });
  const { locale } = useContext(LocaleContext);
  const {
    items: uploadItems,
    progress: uploadProgress,
    status: uploadStatus,
    speed: uploadSpeed,
    timeRemaining,
    visible,
    collapsed,
    // toggleVisibility,
    toggleCollapsed,
    clearCompleted,
    // cancelAllUploads,
    cancelUpload,
  } = state;

  // 항목 카테고리 분류
  const activeItems = uploadItems.filter(
    (item) => uploadStatus[item.uid] === 'uploading' || uploadStatus[item.uid] === 'waiting',
  );

  const completedItems = uploadItems.filter((item) => uploadStatus[item.uid] === 'completed');

  const failedItems = uploadItems.filter(
    (item) => uploadStatus[item.uid] === 'error' || uploadStatus[item.uid] === 'cancelled',
  );

  // 업로드 항목이 없으면 패널을 표시하지 않음
  if (!visible || uploadItems.length === 0) {
    return null;
  }

  // 전체 업로드 상태
  const activeCount = activeItems.length;
  const completedCount = completedItems.length;
  const failedCount = failedItems.length;
  const totalCount = uploadItems.length;

  // 패널 닫기
  const handleClose = () => {
    if (activeCount > 0) {
      // 진행 중인 업로드가 있으면 패널 최소화
      toggleCollapsed(true);
    } else {
      // 완료된 항목만 있으면 정리하고 패널 닫기
      clearCompleted();
    }
  };

  return (
    <UploadPanelContainer>
      <div
        className="upload-progress-panel"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: collapsed ? '200px' : '380px',
          maxHeight: '500px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          transition: 'width 0.3s ease',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <Icon name={<AiOutlineUpload size={18} />} />
                <span>{locale.resource['txt.file-transfer.file-upload']}</span>
                {activeCount > 0 && (
                  <Badge count={activeCount} style={{ backgroundColor: '#1890ff' }} />
                )}
              </Space>
              <Space>
                {!collapsed && (
                  <Button
                    type="text"
                    size="large"
                    icon={<AiOutlineMinusCircle />}
                    onClick={() => toggleCollapsed(true)}
                  />
                )}
                {collapsed && (
                  <Button
                    type="text"
                    size="large"
                    icon={<AiOutlineUpCircle />}
                    onClick={() => toggleCollapsed(false)}
                  />
                )}
                {activeCount === 0 && (
                  <Button
                    type="text"
                    size="large"
                    icon={<AiOutlineCloseCircle />}
                    onClick={handleClose}
                  />
                )}
              </Space>
            </div>
          }
          bodyStyle={{
            padding: collapsed ? '8px' : '12px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {!collapsed && (
            <>
              <div className="upload-overall-status" style={{ marginBottom: '16px' }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                >
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {completedCount}/{totalCount}{' '}
                    {locale.resource['txt.file-transfer.progress-files-complete']}
                  </Text>
                  {/* {failedCount > 0 && (
                    <Text type="danger" style={{ fontSize: '12px' }}>
                      실패: {failedCount} 건
                    </Text>
                  )} */}
                </div>

                {/* {activeCount > 0 && (
                  <Button
                    type="text"
                    // danger
                    size="small"
                    onClick={cancelAllUploads}
                    style={{ padding: 0 }}
                  >
                    모든 업로드 취소
                  </Button>
                )} */}
                {/* {(completedCount > 0 || failedCount > 0) && (
                  <Button
                    type="text"
                    size="small"
                    onClick={clearCompleted}
                    style={{ padding: 0, marginLeft: '12px' }}
                  >
                    완료된 항목 지우기
                  </Button>
                )} */}
              </div>

              {uploadItems.length > 0 && (
                <div className="upload-items">
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    {locale.resource['txt.file-transfer.upload-items']} ({uploadItems.length})
                  </Text>
                  <List
                    size="small"
                    dataSource={uploadItems}
                    renderItem={(item) => {
                      const status = uploadStatus[item.uid];
                      const isCompleted = status === 'completed';
                      const isUploading = status === 'uploading';
                      const isWaiting = status === 'waiting';
                      const isCancelled = status === 'cancelled';

                      // 진행 중인 항목은 진행률 표시
                      const progress = uploadProgress[item.uid]?.progress || 0;

                      return (
                        <List.Item
                          style={{ width: '100%' }}
                          actions={[
                            // 진행 중인 항목만 취소 버튼 표시
                            isUploading || isWaiting ? (
                              <Tooltip title={locale.resource['txt.file-transfer.cancel']}>
                                <Button
                                  type="text"
                                  size="large"
                                  danger
                                  onClick={() => cancelUpload(item.uid)}
                                  icon={<AiOutlineCloseCircle />}
                                />
                              </Tooltip>
                            ) : null,
                            // 완료 아이콘
                            isCompleted ? (
                              <Tooltip title={locale.resource['txt.file-transfer.completed']}>
                                <div style={{ display: 'inline-flex', cursor: 'default' }}>
                                  <AiOutlineCheckCircle
                                    style={{
                                      fontSize: 18,
                                      color: '#52c41a',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            ) : null,
                          ].filter(Boolean)} // null 제거
                        >
                          <List.Item.Meta
                            avatar={<AiOutlineFile style={{ fontSize: 16 }} />}
                            title={<TextEllipsis text={item.name} />}
                            description={
                              isUploading || isWaiting ? (
                                <div style={{ maxWidth: '250px' }}>
                                  <Progress percent={progress} size="small" status="active" />
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      {formatFileSize(item.size)}
                                    </Text>
                                  </div>
                                </div>
                              ) : (
                                <Text style={{ fontSize: '12px' }}>
                                  {formatFileSize(item.size)}
                                </Text>
                              )
                            }
                          />
                          {isCancelled && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {locale.resource['txt.file-transfer.status-cancelled']}
                            </Text>
                          )}
                          {status === 'error' && (
                            <Text type="danger" style={{ fontSize: '12px' }}>
                              {locale.resource['txt.file-transfer.status-error']}
                            </Text>
                          )}
                        </List.Item>
                      );
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* 축소 상태일 때 표시되는 간단한 요약 정보 */}
          {collapsed && (
            <div style={{ padding: '8px 12px' }}>
              <Text style={{ fontSize: '12px' }}>
                {completedCount}/{totalCount} {locale.resource['txt.file-transfer.completed']}
                {activeCount > 0 &&
                  `, ${activeCount} ${locale.resource['txt.file-transfer.progress-in-progress']}`}
                {failedCount > 0 &&
                  `, ${failedCount} ${locale.resource['txt.file-transfer.progress-failed']}`}
              </Text>
            </div>
          )}
        </Card>
      </div>
    </UploadPanelContainer>
  );
};

export default UploadProgressPanel;
